import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { ChevronDown, Camera, X, Coffee, Loader2 } from 'lucide-react'
import { Textarea } from '@/shared/ui/textarea'
import { Input } from '@/shared/ui/input'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'
import { useUpload } from '@/shared/api/use-upload'
import { OptimizedImage } from '@/shared/ui'

export interface DiaryFormData {
  rating: number | null
  mood: string | null           // Настроение: relaxed, focused, energized, happy
  brewingMethod: string | null  // Метод: gongfu, western, grandpa
  notes: string
  userPhotoUrl: string | null
  brewingParams: {
    temperature?: number
    brewingTime?: number
    teaAmount?: number
    waterAmount?: number
  }
}

export interface DiaryFormProps {
  /**
   * Данные формы
   */
  data: DiaryFormData
  /**
   * Callback при изменении данных
   */
  onChange: (data: Partial<DiaryFormData>) => void
  /**
   * Информация о чае (для отображения в шапке)
   */
  teaInfo?: {
    name: string
    category?: string
    subcategory?: string
    imageUrl?: string
    date?: string
  }
  /**
   * Callback для смены чая — если передан, показывается кнопка "Изменить"
   */
  onChangeTea?: () => void
  /**
   * Показывать блок фото
   */
  showPhoto?: boolean
  /**
   * Компактный режим (для BottomSheet)
   */
  compact?: boolean
}

/**
 * Уровни оценки для премиальной шкалы
 * 5 уровней: 1-5 (constraint в БД)
 */
const RATING_LEVELS = [
  { value: 1, label: 'Не моё', emoji: '😔', color: 'bg-red-500' },
  { value: 2, label: 'Пока не понял', emoji: '😐', color: 'bg-amber-500' },
  { value: 3, label: 'Понравилось', emoji: '🙂', color: 'bg-lime-500' },
  { value: 4, label: 'Отлично', emoji: '😊', color: 'bg-green-500' },
  { value: 5, label: 'Лучшее что пил', emoji: '🤩', color: 'bg-emerald-500' },
] as const

/**
 * Настроения
 */
const MOOD_OPTIONS = [
  { value: 'relaxed', label: 'Спокойствие', emoji: '😌' },
  { value: 'focused', label: 'Фокус', emoji: '🎯' },
  { value: 'energized', label: 'Энергия', emoji: '⚡' },
  { value: 'happy', label: 'Радость', emoji: '😊' },
  { value: 'inspired', label: 'Вдохновение', emoji: '✨' },
  { value: 'balanced', label: 'Баланс', emoji: '⚖️' },
  { value: 'cozy', label: 'Уют', emoji: '🕯️' },
  { value: 'thoughtful', label: 'Задумчивость', emoji: '💭' },
] as const

/**
 * Методы заваривания
 */
const BREWING_METHODS = [
  { value: 'gongfu', label: 'Пролив', emoji: '🍵' },
  { value: 'western', label: 'Настаивание', emoji: '☕' },
  { value: 'grandpa', label: 'Свободный', emoji: '🫖' },
] as const

/**
 * Унифицированная форма записи в дневник
 * 
 * Используется в:
 * - QuickAddSheet (compact=true)
 * - TeaDiaryAddPage (compact=false)
 * - TeaDiaryEditPage (compact=false)
 */
export function DiaryForm({
  data,
  onChange,
  teaInfo,
  onChangeTea,
  showPhoto = true,
  compact = false,
}: DiaryFormProps) {
  const { hapticFeedback } = useTelegram()
  const { uploadImage, isUploading } = useUpload()
  const [showBrewingParams, setShowBrewingParams] = useState(
    !!(data.brewingParams.temperature || data.brewingParams.brewingTime || data.brewingParams.teaAmount)
  )

  // Обработка фото - загружаем на сервер
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    hapticFeedback.impactOccurred('light')

    // Загружаем на сервер (хук сам сжимает изображение)
    const url = await uploadImage(file)
    
    if (url) {
      hapticFeedback.notificationOccurred('success')
      onChange({ userPhotoUrl: url })
    } else {
      hapticFeedback.notificationOccurred('error')
    }
    
    // Сбрасываем input чтобы можно было выбрать тот же файл повторно
    e.target.value = ''
  }

  const handleRemovePhoto = () => {
    hapticFeedback.impactOccurred('light')
    onChange({ userPhotoUrl: null })
  }
  
  return (
    <div className={cn('space-y-5', compact ? '' : 'px-4 py-5')}>
      {/* Информация о чае */}
      {teaInfo && (
        <div className="flex items-center gap-3 p-3 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border shadow-sm dark:shadow-none">
          <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gray-100 dark:bg-dark-elevated flex items-center justify-center overflow-hidden">
            {teaInfo.imageUrl ? (
              <OptimizedImage
                src={teaInfo.imageUrl} 
                alt={teaInfo.name}
                preset="thumb"
                wrapperClassName="h-full w-full"
                className="object-cover"
              />
            ) : (
              <Coffee className="w-6 h-6 text-primary dark:text-primary-light" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-ink dark:text-white truncate">
              {teaInfo.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {teaInfo.subcategory 
                ? `${teaInfo.category} · ${teaInfo.subcategory}` 
                : teaInfo.category || teaInfo.date || 'Чай'}
            </p>
          </div>
          {onChangeTea && (
            <button
              type="button"
              onClick={() => {
                hapticFeedback.impactOccurred('light')
                onChangeTea()
              }}
              className="text-primary dark:text-primary-light text-sm font-medium px-3 py-2 rounded-lg hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors flex-shrink-0"
            >
              Изменить
            </button>
          )}
        </div>
      )}
      
      {/* Оценка - 5 уровней 
      <div>
        <label className="block text-sm font-medium text-ink dark:text-white mb-3">
          Впечатление
        </label>
        
        <div className="grid grid-cols-5 gap-2">
          {RATING_LEVELS.map((level) => {
            const isSelected = data.rating === level.value
            
            return (
              <motion.button
                key={level.value}
                type="button"
                onClick={() => {
                  hapticFeedback.impactOccurred('medium')
                  onChange({ rating: data.rating === level.value ? null : level.value })
                }}
                whileTap={{ scale: 0.92 }}
                className={cn(
                  'flex flex-col items-center gap-1.5 py-3 px-1 rounded-2xl transition-all touch-target',
                  'border-2',
                  isSelected
                    ? `${level.color} border-transparent text-white shadow-lg`
                    : 'bg-gray-50 dark:bg-dark-card border-transparent hover:bg-gray-100 dark:hover:bg-dark-elevated'
                )}
              >
                <span className="text-2xl" role="img" aria-label={level.label}>
                  {level.emoji}
                </span>
                <span className={cn(
                  'text-xs font-medium leading-tight text-center',
                  isSelected ? 'text-white' : 'text-gray-600 dark:text-gray-400'
                )}>
                  {level.label}
                </span>
              </motion.button>
            )
          })}
        </div>
        
        <AnimatePresence>
          {data.rating !== null && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2"
            >
              Оценка: {data.rating}/5
            </motion.p>
          )}
        </AnimatePresence>
      </div>*/}

      {/* Настроение */}
      <div>
        <label className="block text-sm font-medium text-ink dark:text-white mb-2">
          Настроение <span className="text-gray-400 dark:text-gray-500 font-normal">(необязательно)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {MOOD_OPTIONS.map((mood) => {
            const isSelected = data.mood === mood.value
            return (
              <motion.button
                key={mood.value}
                type="button"
                onClick={() => {
                  hapticFeedback.selectionChanged()
                  onChange({ mood: isSelected ? null : mood.value })
                }}
                whileTap={{ scale: 0.95 }}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all',
                  isSelected
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-elevated'
                )}
              >
                <span>{mood.emoji}</span>
                <span>{mood.label}</span>
              </motion.button>
            )
          })}
        </div>
      </div>

      {/* Метод заваривания */}
      <div>
        <label className="block text-sm font-medium text-ink dark:text-white mb-2">
          Метод заваривания <span className="text-gray-400 dark:text-gray-500 font-normal">(необязательно)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {BREWING_METHODS.map((method) => {
            const isSelected = data.brewingMethod === method.value
            return (
              <motion.button
                key={method.value}
                type="button"
                onClick={() => {
                  hapticFeedback.selectionChanged()
                  onChange({ brewingMethod: isSelected ? null : method.value })
                }}
                whileTap={{ scale: 0.95 }}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all',
                  isSelected
                    ? 'bg-amber-600 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-elevated'
                )}
              >
                <span>{method.emoji}</span>
                <span>{method.label}</span>
              </motion.button>
            )
          })}
        </div>
      </div>
      
      {/* Заметка */}
      <div>
        <label className="block text-sm font-medium text-ink dark:text-white mb-2">
          Заметка <span className="text-gray-400 dark:text-gray-500 font-normal">(необязательно)</span>
        </label>
        <Textarea
          value={data.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Ваши впечатления..."
          rows={compact ? 2 : 3}
        />
      </div>
      
      {/* Параметры заваривания (расширяемый блок) */}
      <div className="border border-silk dark:border-dark-border rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => {
            hapticFeedback.selectionChanged()
            setShowBrewingParams(!showBrewingParams)
          }}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors"
        >
          <span className="text-sm font-medium text-ink dark:text-white">
            Параметры заваривания
          </span>
          <motion.div
            animate={{ rotate: showBrewingParams ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </motion.div>
        </button>
        
        <AnimatePresence>
          {showBrewingParams && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="p-3 pt-0 grid grid-cols-2 gap-3">
                <Input
                  label="Температура (°C)"
                  type="number"
                  value={data.brewingParams.temperature?.toString() || ''}
                  onChange={(e) => onChange({
                    brewingParams: { ...data.brewingParams, temperature: e.target.value ? Number(e.target.value) : undefined }
                  })}
                  placeholder="80"
                />
                <Input
                  label="Время (сек)"
                  type="number"
                  value={data.brewingParams.brewingTime?.toString() || ''}
                  onChange={(e) => onChange({
                    brewingParams: { ...data.brewingParams, brewingTime: e.target.value ? Number(e.target.value) : undefined }
                  })}
                  placeholder="120"
                />
                <Input
                  label="Чай (г)"
                  type="number"
                  value={data.brewingParams.teaAmount?.toString() || ''}
                  onChange={(e) => onChange({
                    brewingParams: { ...data.brewingParams, teaAmount: e.target.value ? Number(e.target.value) : undefined }
                  })}
                  placeholder="5"
                />
                <Input
                  label="Вода (мл)"
                  type="number"
                  value={data.brewingParams.waterAmount?.toString() || ''}
                  onChange={(e) => onChange({
                    brewingParams: { ...data.brewingParams, waterAmount: e.target.value ? Number(e.target.value) : undefined }
                  })}
                  placeholder="150"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Фото */}
      {showPhoto && (
        <div>
          <label className="block text-sm font-medium text-ink dark:text-white mb-2">
            Фото <span className="text-gray-400 dark:text-gray-500 font-normal">(необязательно)</span>
          </label>
          
          {data.userPhotoUrl ? (
            <div className="relative rounded-xl overflow-hidden">
              <OptimizedImage
                src={data.userPhotoUrl}
                alt="Фото чая"
                preset="hero"
                wrapperClassName="h-48 w-full"
                className="object-cover"
              />
              <button
                type="button"
                onClick={handleRemovePhoto}
                disabled={isUploading}
                className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors touch-target disabled:opacity-50 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className={cn(
              "flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed transition-colors touch-target",
              isUploading 
                ? "border-primary/50 bg-primary/5 cursor-wait"
                : "border-silk dark:border-dark-border hover:border-primary dark:hover:border-primary-light hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer"
            )}>
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  <span className="text-sm text-primary">Загрузка...</span>
                </>
              ) : (
                <>
                  <Camera className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">Добавить фото</span>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                disabled={isUploading}
                className="hidden"
              />
            </label>
          )}
        </div>
      )}
    </div>
  )
}

export default DiaryForm

